import React from 'react';
import PropTypes from 'prop-types';
import { Form, Col, Row } from 'react-bootstrap';
import Datetime from 'react-datetime';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import moment from 'moment';
import { toastr } from 'Helpers/ToastHelper';
import { DATE_FORMAT_ISO, DATE_FORMAT_WEB } from 'Constants/DateFormatConstants';
import { DateTimeFormater } from 'Helpers/DateTimeHelper';
import { processEmailsForDisplay, getAssignmentDateLabel, TUI_EMAIL_REGEX } from 'Helpers/FlightTicketHelper';

const FlightInfoSection = ({
  data = {},
  errors = {},
  onChange = () => {},
  options = {},
  getReactSelectStyles = () => ({}),
}) => {
  // Safely destructure options with defaults
  const { typeOfFlight = [], luggage = [], airports = [] } = options || {};

  // Safely access data with defaults
  const safeData = data || {};
  const safeErrors = errors || {};

  return (
    <>
      <h6 className="text-primary mb-3 mt-4">
        <i className="fa fa-plane me-2"></i>Flight Information
      </h6>
      <Row>
        <Form.Group as={Col}>
          <Form.Label>Planned Assignment {getAssignmentDateLabel(safeData.direction)} Date</Form.Label>
          <Datetime
            timeFormat={false}
            dateFormat={DATE_FORMAT_WEB}
            closeOnSelect
            utc={true}
            inputProps={{ placeholder: DATE_FORMAT_WEB, disabled: true }}
            value={DateTimeFormater(safeData.plannedAssignmentStartDate, null, DATE_FORMAT_WEB)}
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>
            Preferred Flight Date <span className="text-danger">*</span>
          </Form.Label>
          <Datetime
            timeFormat={false}
            dateFormat={DATE_FORMAT_WEB}
            closeOnSelect
            utc={true}
            inputProps={{
              placeholder: DATE_FORMAT_WEB,
              className: safeErrors.preferredFlightDate ? 'form-control is-invalid' : 'form-control',
            }}
            value={DateTimeFormater(safeData.preferredFlightDate, null, DATE_FORMAT_WEB)}
            onChange={(e) => {
              if (moment.isMoment(e) && e.isValid() && onChange) {
                onChange({ preferredFlightDate: e.format(DATE_FORMAT_ISO) });
              }
            }}
          />
          {safeErrors.preferredFlightDate && (
            <div className="invalid-feedback d-block">Preferred flight date is required</div>
          )}
        </Form.Group>
      </Row>
      <Row>
        <Form.Group as={Col}>
          <Form.Label>
            Type of Flight <span className="text-danger">*</span>
          </Form.Label>
          <Select
            options={typeOfFlight}
            isSearchable
            isClearable
            styles={getReactSelectStyles ? getReactSelectStyles(!!safeErrors.typeOfFlight) : {}}
            onChange={(e) => onChange && onChange({ typeOfFlight: e?.value || '' })}
            value={typeOfFlight.find((x) => x.value === safeData.typeOfFlight) || null}
            placeholder="Select flight type"
          />
          {safeErrors.typeOfFlight && <div className="invalid-feedback d-block">Flight type is required</div>}
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>
            Staff Luggage <span className="text-danger">*</span>
          </Form.Label>
          <Select
            options={luggage}
            isSearchable
            isClearable
            styles={getReactSelectStyles ? getReactSelectStyles(!!safeErrors.luggage) : {}}
            onChange={(e) => onChange && onChange({ luggage: e?.value || '' })}
            value={luggage.find((x) => x.value === safeData.luggage) || null}
            placeholder="Select luggage"
          />
          {safeErrors.luggage && <div className="invalid-feedback d-block">Luggage is required</div>}
        </Form.Group>
      </Row>
      <Row>
        <Form.Group as={Col}>
          <Form.Label>Additional Notification Emails</Form.Label>
          <CreatableSelect
            isSearchable
            isClearable
            isMulti
            styles={getReactSelectStyles ? getReactSelectStyles(false) : {}}
            onChange={(e) => {
              if (!onChange) return;

              if (!e) {
                onChange({ additionNotificationEmails: [] });
                return;
              }
              const emailRegex = TUI_EMAIL_REGEX;
              if (!e.every((email) => emailRegex.test(email.value))) {
                const invalidEmail = e[e.length - 1];
                toastr.warning('Invalid Email', `${invalidEmail.value} must be a @tui.com or @tui.de email`);
                return;
              }
              onChange({ additionNotificationEmails: e?.map((x) => x.value) || [] });
            }}
            value={processEmailsForDisplay(safeData.additionNotificationEmails)}
            placeholder="Add notification emails"
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>
            Departure Airports <span className="text-danger">*</span>
          </Form.Label>
          <Select
            options={airports}
            isSearchable
            isClearable
            isMulti
            styles={getReactSelectStyles ? getReactSelectStyles(!!safeErrors.departureAirports) : {}}
            onChange={(e) => onChange && onChange({ departureAirports: e?.map((x) => x.value) || [] })}
            value={airports
              .filter((x) => safeData.departureAirports?.includes(x.value))
              .sort(
                (a, b) => safeData.departureAirports.indexOf(a.value) - safeData.departureAirports.indexOf(b.value)
              )}
            isDisabled={safeData.noFlightNeeded}
            placeholder="Select departure airports"
          />
          {safeErrors.departureAirports && <div className="invalid-feedback d-block">Departure airports required</div>}
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>
            Arrival Airports <span className="text-danger">*</span>
          </Form.Label>
          <Select
            options={airports}
            isSearchable
            isClearable
            isMulti
            styles={getReactSelectStyles ? getReactSelectStyles(!!safeErrors.arrivalAirports) : {}}
            onChange={(e) => onChange && onChange({ arrivalAirports: e?.map((x) => x.value) || [] })}
            value={airports
              .filter((x) => safeData.arrivalAirports?.includes(x.value))
              .sort((a, b) => safeData.arrivalAirports.indexOf(a.value) - safeData.arrivalAirports.indexOf(b.value))}
            isDisabled={safeData.noFlightNeeded}
            placeholder="Select arrival airports"
          />
          {safeErrors.arrivalAirports && <div className="invalid-feedback d-block">Arrival airports required</div>}
        </Form.Group>
      </Row>
    </>
  );
};

FlightInfoSection.propTypes = {
  data: PropTypes.object,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  options: PropTypes.object,
  getReactSelectStyles: PropTypes.func,
};

export default FlightInfoSection;
